// src/pages/LoginPage.jsx
import { useState, useRef } from "react";
import { StarField, FormInput, Btn, PasswordChecklist, isPasswordStrong, ResponsiveHCaptcha } from "../components/UI";
import { supabase } from "../lib/supabase";

const HCAPTCHA_SITE_KEY = "c142c84c-5e1e-4bfa-8032-f48e0ff29bac";
const CONSENT_VERSION = "V1.0";

const CONSENT_TEXT = `MARS ACADEMY — PARENT & GUARDIAN ENROLLMENT AGREEMENT
Version 1.0

By creating an account, I confirm that I am the parent or legal guardian of the child(ren) associated with this account and that I am authorized to enroll them in this program operated by MARS Academy. I consent to the collection, use, and storage of my information and my child's information, including name, grade level, and any provided allergy or accommodation details, for the purposes of enrollment, attendance tracking, communication, and student safety.

I acknowledge that this information will be stored securely and used only for legitimate program operations. I understand that I may request correction or deletion of my or my child's personal information at any time.

Program Participation & Supervision: I understand that MARS Academy provides structured educational activities in an after-school learning environment. While reasonable supervision will be provided during scheduled program hours, I acknowledge that I am responsible for ensuring timely drop-off and pickup (or authorized dismissal) of my child. I understand that MARS Academy is not responsible for supervision outside of designated program hours.

Technology Use & Responsibility: I understand that MARS Academy provides instruction in technology and digital skills in a supervised educational setting. I acknowledge that any use of computers, devices, software, or the internet by my child outside of the program—including at home or on personal devices—is solely my responsibility as the parent or guardian. MARS Academy does not monitor or control student activity outside of program hours and is not responsible for any actions, content accessed, or outcomes resulting from use of technology outside of its supervised instruction.

Medical Disclaimer: I understand that MARS Academy is not a medical provider and does not provide medical advice, diagnosis, or treatment. While reasonable efforts will be made to consider disclosed allergies and special needs, I acknowledge that MARS Academy cannot guarantee the prevention of all allergic reactions or medical incidents. I agree that it is my responsibility to provide accurate, complete, and up-to-date medical and allergy information and to notify MARS Academy of any changes.

Assumption of Risk & Liability Waiver: I understand that participation in after-school programs and related activities may involve inherent risks, including but not limited to minor injuries, allergic reactions, or other unforeseen incidents. I voluntarily assume all such risks on behalf of my child. To the fullest extent permitted by law, I agree to release, waive, and hold harmless MARS Academy, its owners, employees, instructors, and affiliates from any and all claims, liabilities, damages, or expenses arising out of or related to my child's participation, except in cases of gross negligence or willful misconduct.

Emergency & Communication Consent: I authorize MARS Academy to contact me using the information provided for program-related communication. In the event of an emergency, I authorize staff to seek appropriate medical assistance for my child if I cannot be reached.

I further acknowledge and provide verifiable parental consent in accordance with the Children's Online Privacy Protection Act, and I agree to the Terms of Service and Privacy Policy.`;

export default function LoginPage({ setPage, setUser }) {
  const [tab, setTab] = useState("login");

  // ── Login state
  const [lEmail,   setLEmail]   = useState("");
  const [lPass,    setLPass]    = useState("");
  const [lError,   setLError]   = useState("");
  const [lLoading, setLLoading] = useState(false);
  const [lCaptchaToken, setLCaptchaToken] = useState("");
  const lCaptchaRef = useRef(null);

  // ── Sign up state
  const [sName,    setSName]    = useState("");
  const [sEmail,   setSEmail]   = useState("");
  const [sPhone,   setSPhone]   = useState("");
  const [sPass,    setSPass]    = useState("");
  const [sPass2,   setSPass2]   = useState("");
  const [sConsent, setSConsent] = useState(false);
  const [sError,   setSError]   = useState("");
  const [sLoading, setSLoading] = useState(false);
  const [sConfirm, setSConfirm] = useState(false);
  const [sCaptchaToken, setSCaptchaToken] = useState("");
  const sCaptchaRef = useRef(null);

  async function handleLogin() {
    setLError("");
    if (!lCaptchaToken) { setLError("Please complete the CAPTCHA."); return; }
    setLLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email: lEmail, password: lPass, options: { captchaToken: lCaptchaToken } });
    setLLoading(false);
    lCaptchaRef.current?.resetCaptcha();
    setLCaptchaToken("");
    if (error) { setLError(error.message); return; }

    const { data: profileData } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", data.user.id)
      .single();

    if (!profileData) { setLError("Account not found. Please create an account first."); return; }

    // Sync consent from user_metadata → profiles if the email-confirmation
    // flow prevented the upsert from running during sign-up.
    let profile = profileData;
    const meta = data.user.user_metadata ?? {};
    if (meta.consent_agreed_at && !profile.consent_agreed_at) {
      await supabase.from("profiles").update({
        consent_agreed_at: meta.consent_agreed_at,
        consent_version: meta.consent_version ?? CONSENT_VERSION,
      }).eq("id", data.user.id);
      profile = { ...profile, consent_agreed_at: meta.consent_agreed_at, consent_version: meta.consent_version ?? CONSENT_VERSION };
    }

    setUser({ ...profile, students: [] });
    setPage("dashboard");
  }

  async function handleSignUp() {
    setSError("");
    if (!sName || !sEmail || !sPhone || !sPass) { setSError("Name, email, phone, and password are required."); return; }
    if (sPass !== sPass2) { setSError("Passwords do not match."); return; }
    if (!isPasswordStrong(sPass)) { setSError("Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a symbol."); return; }
    if (!sConsent) { setSError("You must read and agree to the Enrollment Agreement to create an account."); return; }
    if (!sCaptchaToken) { setSError("Please complete the CAPTCHA."); return; }

    setSLoading(true);
    const consentAt = new Date().toISOString();

    // Store consent in user_metadata so it survives the email-confirmation
    // flow even when no session exists after sign-up.
    const { data, error } = await supabase.auth.signUp({
      email: sEmail,
      password: sPass,
      options: { data: { name: sName, phone: sPhone, consent_agreed_at: consentAt, consent_version: CONSENT_VERSION }, captchaToken: sCaptchaToken },
    });
    sCaptchaRef.current?.resetCaptcha();
    setSCaptchaToken("");
    if (error) { setSLoading(false); setSError(error.message); return; }

    // Also write directly to profiles — succeeds when a session is present
    // (i.e. email confirmation is disabled). If RLS blocks it (no session),
    // handleLogin will sync from user_metadata on first sign-in.
    await supabase.from("profiles").upsert({
      id: data.user.id,
      name: sName,
      email: sEmail,
      phone: sPhone,
      consent_agreed_at: consentAt,
      consent_version: CONSENT_VERSION,
    });
    setSLoading(false);

    if (!data.session) { setSConfirm(true); return; }

    const { data: profile } = await supabase.from("profiles").select("*").eq("id", data.user.id).single();
    setUser({ id: data.user.id, name: sName, email: sEmail, phone: sPhone, ...profile, students: [] });
    setPage("dashboard");
  }

  const tabBtn = (id, label) => (
    <button
      onClick={() => { setTab(id); setLError(""); setSError(""); }}
      style={{
        flex: 1, padding: "10px 0", border: "none", cursor: "pointer",
        background: tab === id ? "rgba(255,217,61,0.14)" : "transparent",
        color: tab === id ? "#FFD93D" : "rgba(255,255,255,0.4)",
        fontFamily: "inherit", fontWeight: 800, fontSize: "0.9rem",
        borderBottom: tab === id ? "2px solid #FFD93D" : "2px solid transparent",
        transition: "all 0.2s",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "7rem 2rem 3rem", position: "relative", overflow: "hidden" }}>
      <StarField count={40} />
      <div style={{ background: "rgba(10,13,48,0.9)", border: "1.5px solid rgba(255,255,255,0.12)", borderRadius: 28, padding: "2.6rem", maxWidth: 440, width: "100%", position: "relative", zIndex: 2, backdropFilter: "blur(14px)" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "1.6rem" }}>
          <span style={{ fontSize: "2rem" }}>🚀</span>
          <h2 style={{ fontFamily: "'Fredoka One',cursive", fontSize: "1.9rem", marginTop: "0.4rem" }}>Parent Portal</h2>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.84rem", fontWeight: 600, marginTop: 3 }}>Manage your child's enrollment</p>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.1)", marginBottom: "1.6rem" }}>
          {tabBtn("login",  "Sign In")}
          {tabBtn("signup", "Create Account")}
        </div>

        {/* ── LOGIN TAB ── */}
        {tab === "login" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
            <FormInput label="Email Address" type="email" value={lEmail} onChange={(e) => setLEmail(e.target.value)} placeholder="parent@email.com" required onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
            <FormInput label="Password" type="password" value={lPass} onChange={(e) => setLPass(e.target.value)} placeholder="••••••••" required onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
            <div style={{ background: "rgba(255,255,255,0.04)", border: "1.5px solid rgba(255,255,255,0.12)", borderRadius: 14, padding: "0.9rem" }}>
              <ResponsiveHCaptcha ref={lCaptchaRef} sitekey={HCAPTCHA_SITE_KEY} theme="dark" onVerify={(token) => setLCaptchaToken(token)} onExpire={() => setLCaptchaToken("")} />
            </div>
            {lError && <p style={{ color: "#FF6B6B", fontSize: "0.82rem", fontWeight: 700, margin: 0 }}>{lError}</p>}
            <Btn onClick={handleLogin} disabled={lLoading || !lCaptchaToken} variant="primary" style={{ width: "100%", marginTop: "0.2rem" }}>
              {lLoading ? "Signing in…" : "Sign In →"}
            </Btn>
            <p style={{ textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: "0.8rem", fontWeight: 600 }}>
              No account?{" "}
              <button onClick={() => setTab("signup")} style={{ background: "none", border: "none", color: "#FFD93D", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: "0.8rem" }}>
                Create one →
              </button>
            </p>
          </div>
        )}

        {/* ── SIGN UP TAB ── */}
        {tab === "signup" && !sConfirm && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
            <FormInput label="Full Name" value={sName} onChange={(e) => setSName(e.target.value)} placeholder="Jane Smith" required />
            <FormInput label="Email Address" type="email" value={sEmail} onChange={(e) => setSEmail(e.target.value)} placeholder="parent@email.com" required />
            <FormInput label="Phone Number" value={sPhone} onChange={(e) => setSPhone(e.target.value)} placeholder="(555) 000-0000" required />
            <div>
              <FormInput label="Password" type="password" value={sPass} onChange={(e) => setSPass(e.target.value)} placeholder="Create a password" required />
              {sPass.length > 0 && <PasswordChecklist password={sPass} />}
            </div>
            <FormInput label="Confirm Password" type="password" value={sPass2} onChange={(e) => setSPass2(e.target.value)} placeholder="••••••••" required onKeyDown={(e) => e.key === "Enter" && handleSignUp()} />

            {/* ── Enrollment Agreement ── */}
            <div style={{ border: `1.5px solid ${sConsent ? "rgba(34,197,94,0.45)" : "rgba(255,255,255,0.14)"}`, borderRadius: 12, overflow: "hidden", transition: "border-color 0.2s" }}>
              <div style={{ background: "rgba(255,255,255,0.04)", padding: "8px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "rgba(255,255,255,0.55)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Enrollment Agreement</span>
                <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#FFD93D", letterSpacing: "0.06em" }}>V1.0</span>
              </div>
              <div style={{ maxHeight: 158, overflowY: "auto", padding: "12px 14px", fontSize: "0.71rem", lineHeight: 1.7, color: "rgba(255,255,255,0.48)", whiteSpace: "pre-wrap", fontWeight: 500, scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.15) transparent" }}>
                {CONSENT_TEXT}
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", borderTop: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", background: sConsent ? "rgba(34,197,94,0.07)" : "rgba(255,255,255,0.02)", transition: "background 0.2s" }}>
                <input
                  type="checkbox"
                  checked={sConsent}
                  onChange={(e) => setSConsent(e.target.checked)}
                  style={{ marginTop: 2, accentColor: "#22C55E", width: 15, height: 15, flexShrink: 0, cursor: "pointer" }}
                />
                <span style={{ fontSize: "0.74rem", fontWeight: 700, color: sConsent ? "#22C55E" : "rgba(255,255,255,0.5)", lineHeight: 1.5, transition: "color 0.2s" }}>
                  I have read and agree to the Enrollment Agreement above. I confirm I am the parent or legal guardian and consent on behalf of my child(ren).
                </span>
              </label>
            </div>

            <div style={{ background: "rgba(255,255,255,0.04)", border: "1.5px solid rgba(255,255,255,0.12)", borderRadius: 14, padding: "0.9rem" }}>
              <ResponsiveHCaptcha ref={sCaptchaRef} sitekey={HCAPTCHA_SITE_KEY} theme="dark" onVerify={(token) => setSCaptchaToken(token)} onExpire={() => setSCaptchaToken("")} />
            </div>
            {sError && <p style={{ color: "#FF6B6B", fontSize: "0.82rem", fontWeight: 700, margin: 0 }}>{sError}</p>}
            <Btn onClick={handleSignUp} disabled={sLoading || !sConsent || !sCaptchaToken} variant="success" style={{ width: "100%", marginTop: "0.2rem", opacity: (sConsent && sCaptchaToken) ? 1 : 0.45, transition: "opacity 0.2s" }}>
              {sLoading ? "Creating account…" : "Create Account 🌟"}
            </Btn>
          </div>
        )}

        {/* ── EMAIL CONFIRMATION MESSAGE ── */}
        {tab === "signup" && sConfirm && (
          <div style={{ textAlign: "center", padding: "1rem 0" }}>
            <div style={{ fontSize: "3rem", marginBottom: "0.8rem" }}>📬</div>
            <h3 style={{ fontFamily: "'Fredoka One',cursive", fontSize: "1.4rem", color: "#FFD93D", marginBottom: "0.6rem" }}>Check your inbox!</h3>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.88rem", fontWeight: 600, lineHeight: 1.7 }}>
              We sent a confirmation link to <strong style={{ color: "#fff" }}>{sEmail}</strong>. Click it to activate your account, then sign in.
            </p>
            <Btn onClick={() => { setSConfirm(false); setTab("login"); }} variant="ghost" style={{ marginTop: "1.2rem" }}>
              Back to Sign In
            </Btn>
          </div>
        )}

        <button
          onClick={() => setPage("home")}
          style={{ display: "block", width: "100%", background: "none", border: "none", color: "rgba(255,255,255,0.3)", fontSize: "0.8rem", cursor: "pointer", fontFamily: "inherit", fontWeight: 600, marginTop: "1.2rem", textAlign: "center" }}
        >
          ← Back to main site
        </button>
      </div>
    </div>
  );
}
